import { Component, OnInit, Input } from "@angular/core";

import { ItemsListComponent } from "../items-list/items-list.component";
import { DocumentsService } from "../../../_services/documents.service";
import { DocumentItem } from "../../../_models/document";
import { AlertService } from "@modules/alert";
import { NgxSmartModalService } from "ngx-smart-modal";
import { WindowResizeHelperService } from "src/app/_services/window-resize-helper.service";
import { MatDialog } from "@angular/material/dialog";
import { LoadingTypeEnum } from "src/app/modules/shared/_enums/loading-type.enum";
import { finalize } from "rxjs";

@Component({
  selector: "app-documents",
  templateUrl: "./documents.component.html",
  styleUrls: ["./documents.component.css"],
})
export class DocumentsComponent extends ItemsListComponent implements OnInit {
  public items: Array<DocumentItem>;
  public downloadItems: Array<DocumentItem>;
  isXs: boolean;
  public availableFilters = [];
  isFiltersLoaded: boolean = false;

  @Input() entityType;
  @Input() entityId;
  @Input() hideTitle;
  @Input() docType;

  constructor(
    protected service: DocumentsService,
    protected dialog: MatDialog,
    protected alertService: AlertService,
    public ngxSmartModalService: NgxSmartModalService,
    private windowResizeHelper: WindowResizeHelperService
  ) {
    super();
    this.actions = [{ name: "Delete", action: "bulkDelete" }];
  }

  ngOnInit() {
    if (this.docType == "invoices") {
      this.setAvailableFilters();
    }
    this.getItems();

    this.windowResizeHelper.getData().subscribe((data) => {
      this.isXs = data.responsiveStatus;
    });
  }

  public setAvailableFilters() {
    this.service.getFilterOrderInvoicesStatuses().subscribe((data) => {
      const status_filter = {
        id: "status",
        label: "DOCUMENTS.INVOICE-STATUS",
        data: data.result,
      };
      this.availableFilters.push(status_filter);
    });
  }

  public getItems(page: number = 1) {
    if (this.request_call) {
      this.request_call.unsubscribe();
    }

    let params = this.getParams(page);
    delete params["download_url"];
    params = this.prepareInvoiceFilterParams(params);

    this.isLoadingGeneral = true;
    this.request_call = this.service
      .getDocuments(this.entityType, this.entityId, this.docType, params)
      .pipe(
        finalize(() => {
          (this.isLoadingGeneral = false), (this.isFiltersLoaded = true);
        })
      )
      .subscribe(
        (data) => {
          this.processResponse(data);
          if (this.isOrderDocument) {
            // Order documents (xpath-documents) already return each
            // document's files (with a resolved S3 url) on the list
            // response. The old download_url=1 re-query is a xpath-files
            // convention the documents backend doesn't honour, so build
            // downloadItems straight from what we already fetched instead.
            this.downloadItems = this.items.map((item) => ({
              ...item,
              file:
                item.files && item.files.length
                  ? item.files[0].file_path
                  : null,
            }));
          } else {
            this.downloadItems = new Array<DocumentItem>();
            this.getItemsWithDownloadUrl();
          }
          this.isFiltersLoaded = true;
        },
        (error) => {
          if (error?.error?.errors) {
            this.alertService.errors(error.error.errors);
          }
        }
      );
  }

  getItemsWithDownloadUrl(page: number = 1) {
    if (this.request_call) {
      this.request_call.unsubscribe();
    }

    let params = this.getParams(page);
    params["download_url"] = 1;
    params = this.prepareInvoiceFilterParams(params);

    this.isLoadingGeneral = true;
    this.request_call = this.service
      .getDocuments(this.entityType, this.entityId, this.docType, params)
      .subscribe(
        (data) => {
          this.downloadItems = data.result.items;
          this.isLoadingGeneral = false;
        },
        (error) => {
          this.isLoadingGeneral = false;
          if (error.error.errors) {
            this.alertService.errors(error.error.errors);
          }
        }
      );
  }

  public openInBrowser(doc: DocumentItem) {
    window.open(doc.file, "_blank");
  }

  uploadFile(event) {
    const files = event.target.files;
    this.service
      .uploadFiles(this.entityId, this.docType, files, this.entityType)
      .subscribe(
        (res) => {
          this.getItems();
          if (res.message) {
            this.alertService.success(res.message);
          }
        },
        (error) => {
          this.isLoadingGeneral = false;
          if (error.error.errors) {
            this.alertService.errors(error.error.errors);
          }
        }
      );
  }

  public bulkDelete() {
    console.log("bulk delete called");
  }

  openDocModal() {
    this.ngxSmartModalService.getModal("addDoc").open();
  }

  deleteDocument(id) {
    this.service.deleteDocument(id, this.docType).subscribe((data) => {
      this.getItems();
    });
  }

  /**
   * True only for order DOCUMENTS (xpath-documents ids), not for the order
   * INVOICES tab, which renders this same component with entityType 'orders'
   * but docType 'invoices' and files/billing ids. Delegates to the shared
   * DocumentsService.isOrderDocumentsRequest() predicate so the list, delete
   * and download branches cannot disagree.
   */
  public get isOrderDocument(): boolean {
    return this.service.isOrderDocumentsRequest(this.entityType, this.docType);
  }

  prepareInvoiceFilterParams(params) {
    if (params["status"] == "null" || params["status"] == null) {
      delete params["status"];
    }
    if (this.invoiceIssueDate) {
      if (
        this.invoiceIssueDate.invoice_issue_start_date &&
        this.invoiceIssueDate.invoice_issue_end_date
      ) {
        params["invoice_issue_start_date"] = this.formatDataParam(
          this.invoiceIssueDate.invoice_issue_start_date
        );
        params["invoice_issue_end_date"] = this.formatDataParam(
          this.invoiceIssueDate.invoice_issue_end_date
        );
      }

      if (
        this.invoiceIssueDate.invoice_issue_start_date === "null" &&
        this.invoiceIssueDate.invoice_issue_end_date === "null"
      ) {
        delete params["invoice_issue_start_date"];
        delete params["invoice_issue_end_date"];
      }
    }

    if (this.invoiceDueDate) {
      if (
        this.invoiceDueDate.invoice_due_start_date &&
        this.invoiceDueDate.invoice_due_end_date
      ) {
        params["invoice_due_start_date"] = this.formatDataParam(
          this.invoiceDueDate.invoice_due_start_date
        );
        params["invoice_due_end_date"] = this.formatDataParam(
          this.invoiceDueDate.invoice_due_end_date
        );
      }

      if (
        this.invoiceDueDate.invoice_due_start_date === "null" &&
        this.invoiceDueDate.invoice_due_end_date === "null"
      ) {
        delete params["invoice_due_start_date"];
        delete params["invoice_due_end_date"];
      }
    }

    if (this.invoicePaymentDate) {
      if (
        this.invoicePaymentDate.invoice_payment_start_date &&
        this.invoicePaymentDate.invoice_payment_end_date
      ) {
        params["invoice_payment_start_date"] = this.formatDataParam(
          this.invoicePaymentDate.invoice_payment_start_date
        );
        params["invoice_payment_end_date"] = this.formatDataParam(
          this.invoicePaymentDate.invoice_payment_end_date
        );
      }

      if (
        this.invoicePaymentDate.invoice_payment_start_date === "null" &&
        this.invoicePaymentDate.invoice_payment_end_date === "null"
      ) {
        delete params["invoice_payment_start_date"];
        delete params["invoice_payment_end_date"];
      }
    }

    if (this.docType === "invoices") {
      params["search_fields"] = "invoice_number";
    }

    return params;
  }

  public setFilters(e) {
    this.defaultFilters = {
      ...this.defaultFilters,
      ...e,
    };
    return this.cleanFilters(this.defaultFilters);
  }

  public cleanFilters(obj) {
    for (const propName in obj) {
      if (obj[propName] === null) {
        delete obj[propName];
      }
    }
    return obj;
  }

  get LoadingType() {
    return LoadingTypeEnum;
  }
}
